import { useCallback } from 'react';
import { buildCellPositionMap, getCellAt, getHeaderRowCount } from '@/lib/table-utils';
import {
    deleteColumn,
    deleteRow,
    insertColumn,
    insertRow,
    mergeCells,
    setCellsColor,
    unmergeCells,
} from './TableActions';
import { expandSelectionToMerged } from './TableSelection';
import type { CellPosition, SelectionRange } from './TableSelection';
import type { TableBlock, TableCellColor } from '@/types/report-payload';

type SelectionRect = {
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
};

type UseTableActionsProps = {
    block: TableBlock;
    columnWidths: number[];
    selection: SelectionRange | null;
    selectionRect: SelectionRect | null;
    cellIdToPosition: Map<string, CellPosition>;
    getOriginPosition: (cellId: string) => CellPosition | null;
    updateBlock: (next: TableBlock) => void;
    setSelection: (selection: SelectionRange | null) => void;
};

type UseTableActionsResult = {
    handleMerge: () => void;
    handleUnmerge: () => void;
    handleInsertRowAbove: () => void;
    handleInsertRowBelow: () => void;
    handleDeleteRows: () => void;
    handleInsertColumnLeft: () => void;
    handleInsertColumnRight: () => void;
    handleDeleteColumns: () => void;
    handleAddColumn: () => void;
    handleSetColor: (color?: TableCellColor) => void;
};

export const useTableActions = ({
    block,
    columnWidths,
    selection,
    selectionRect,
    cellIdToPosition,
    getOriginPosition,
    updateBlock,
    setSelection,
}: UseTableActionsProps): UseTableActionsResult => {
    const headerRowCount = getHeaderRowCount(block);

    const handleMerge = useCallback(() => {
        if (!selection) return;
        // Merging a cross-section (header + body) selection would give the
        // header cell a rowSpan that invades the body. Guard the keyboard path
        // (Cmd/Ctrl+M) that bypasses the `canMerge` UI gate.
        if (selection.start.section !== selection.end.section) return;
        const expanded = expandSelectionToMerged(
            selection,
            (section, rowIndex, colIndex) => getCellAt(block, section, rowIndex, colIndex),
            getOriginPosition,
            headerRowCount,
        );

        const next = mergeCells(block, expanded, cellIdToPosition);
        updateBlock(next);
        setSelection(expanded);
    }, [
        block,
        cellIdToPosition,
        getOriginPosition,
        headerRowCount,
        selection,
        setSelection,
        updateBlock,
    ]);

    const handleUnmerge = useCallback(() => {
        if (!selection) return;
        const next = unmergeCells(block, selection, cellIdToPosition);
        updateBlock(next);
    }, [block, cellIdToPosition, selection, updateBlock]);

    const handleInsertRowAbove = useCallback(() => {
        if (!selectionRect || !selection) return;
        const next = insertRow(
            block,
            selection.start.section,
            selectionRect.rowStart,
            cellIdToPosition,
        );
        updateBlock(next);
    }, [block, cellIdToPosition, selection, selectionRect, updateBlock]);

    const handleInsertRowBelow = useCallback(() => {
        if (!selectionRect || !selection) return;
        const section = selection.start.section;
        // No cabeçalho, "abaixo" para na folha: ela tem de continuar sendo a
        // última linha. `insertRow` clampa de novo, para o atalho de teclado.
        const at =
            section === 'header'
                ? Math.min(selectionRect.rowEnd + 1, headerRowCount - 1)
                : selectionRect.rowEnd + 1;
        const next = insertRow(block, section, at, cellIdToPosition);
        updateBlock(next);
    }, [block, cellIdToPosition, headerRowCount, selection, selectionRect, updateBlock]);

    const handleDeleteRows = useCallback(() => {
        if (!selectionRect || !selection) return;
        const section = selection.start.section;
        let next = block;
        for (let r = selectionRect.rowEnd; r >= selectionRect.rowStart; r -= 1) {
            const map = buildCellPositionMap(next);
            next = deleteRow(next, section, r, map);
        }
        updateBlock(next);
    }, [block, selection, selectionRect, updateBlock]);

    const handleInsertColumnLeft = useCallback(() => {
        if (!selectionRect) return;
        const next = insertColumn(block, selectionRect.colStart, cellIdToPosition);
        updateBlock(next);
    }, [block, cellIdToPosition, selectionRect, updateBlock]);

    const handleInsertColumnRight = useCallback(() => {
        if (!selectionRect) return;
        const next = insertColumn(block, selectionRect.colEnd + 1, cellIdToPosition);
        updateBlock(next);
    }, [block, cellIdToPosition, selectionRect, updateBlock]);

    const handleDeleteColumns = useCallback(() => {
        if (!selectionRect) return;
        let next = block;
        for (let c = selectionRect.colEnd; c >= selectionRect.colStart; c -= 1) {
            const map = buildCellPositionMap(next);
            next = deleteColumn(next, c, map);
        }
        updateBlock(next);
    }, [block, selectionRect, updateBlock]);

    const handleSetColor = useCallback(
        (color?: TableCellColor) => {
            if (!selection) return;
            updateBlock(setCellsColor(block, selection, cellIdToPosition, color));
        },
        [block, cellIdToPosition, selection, updateBlock],
    );

    const handleAddColumn = useCallback(() => {
        const map = buildCellPositionMap(block);
        const next = insertColumn(block, columnWidths.length, map);
        updateBlock(next);
    }, [block, columnWidths.length, updateBlock]);

    return {
        handleMerge,
        handleUnmerge,
        handleInsertRowAbove,
        handleInsertRowBelow,
        handleDeleteRows,
        handleInsertColumnLeft,
        handleInsertColumnRight,
        handleDeleteColumns,
        handleAddColumn,
        handleSetColor,
    };
};
